import {
	Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
	FormControl, InputLabel, ListItemText, MenuItem, Select, Stack, TextField
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import "dayjs/locale/en"; // 导入中文语言包
import "dayjs/locale/zh"; // 导入中文语言包
import { addKey, updateKey } from "api/layer";
import { AppAction, AppContext } from "helpers/context";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./add-modal.module.scss";

interface Props {
	open: boolean;
	onClose: () => void;
	onSave: () => void;
	data?: FormData;
	clientData: any[];
	scopeData: any[];
}

interface FormData {
	id?: string;
	clientId: string;
	keyCode: string;
	scope: string;
	idaasClientId: string;
	idaasClientSecrect: string;
	resetTime: any;
	scopeId: string | string[];
	startTime: any;
	endTime: any;
	remark: string;
}

function AddModal(props: Props) {
	const [t] = useTranslation();
	const [context, dispatch] = useContext(AppContext);
	const [formData, setFormData] = useState<FormData>(
		props.data
			? {
				...props.data,
				scopeId: (() => {
					if (!props.data.scopeId) return [];
					if (typeof props.data.scopeId === 'string') {
						return props.data.scopeId.split(',');
					}
					return Array.isArray(props.data.scopeId) ? props.data.scopeId : [];
				})(),
				resetTime: dayjs(props.data.resetTime),
				startTime: dayjs(props.data.startTime),
				endTime: dayjs(props.data.endTime),
			}
			: {
				clientId: "",
				keyCode: "",
				scope: "",
				idaasClientId: "",
				idaasClientSecrect: "",
				scopeId: [],
				resetTime: null,
				startTime: null,
				endTime: null,
				remark: "",
			}
	);
	const [isError, setIsError] = useState(false);

	const handleChange = (event: any) => {
		const { name, value, type, checked } = event.target;
		if (name === "scopeId") {
			setFormData((prevData) => ({ ...prevData, [name]: value === 'string' ? value.split(',') : value }));
		} else {
			setFormData((prevData) => ({ ...prevData, [name]: value }));
		}
	};

	const close = () => {
		props.onClose();
	};

	const handleSave = () => {
		let data = { ...formData };
		if (
			!data.clientId
			|| !data.keyCode
			|| !data.scope
			|| !data.idaasClientId
			|| !data.idaasClientSecrect
			|| !data.resetTime
			|| !data.startTime
			|| !data.endTime
			|| !data.scopeId?.length
		) {
			setIsError(true);
			return;
		}
		setIsError(false);

		data.startTime = formData.startTime.format("YYYY-MM-DD");
		data.endTime = formData.endTime.format("YYYY-MM-DD");
		data.resetTime = formData.resetTime.format("YYYY-MM-DD");

		if (props.data) {
			updateKey(data)
				.then((res: any) => {
					if (res && res.code === 200) {
						props.onClose();
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "editKey",
								message: "editKeySuccess",
							},
						});
					} else {
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "editKey",
								titleIconColor: "yellow",
								titleIcon: "warning",
								resMessage: res.message,
							},
						});
					}
				})
				.finally(() => {
					props.onSave();
				});
		} else {
			addKey(data)
				.then((res: any) => {
					if (res && res.code === 200) {
						props.onClose();
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "addKey",
								message: "addKeySuccess",
							},
						});
					} else {
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "addKey",
								titleIconColor: "yellow",
								titleIcon: "warning",
								resMessage: res.message,
							},
						});
					}
				})
				.finally(() => {
					props.onSave();
				});
		}
	};

	useEffect(() => {
		//// 因为mui的日期组件Previous month和Previous month无法进行中英翻译 删除上月和下月切换按钮的title属性
		const observer = new MutationObserver((mutationsList) => {
			mutationsList.forEach((mutation) => {
				if (mutation.type === "childList") {
					const buttons = document.querySelectorAll(".MuiPickersArrowSwitcher-root button");
					buttons.forEach((button) => {
						button.removeAttribute("title");
					});
				}
			});
		});

		observer.observe(document.body, { childList: true, subtree: true });

		return () => {
			observer.disconnect();
		};
	}, []);

	return (
		<Dialog
			open={props.open}
			onClose={close}
		>
			<DialogTitle
				sx={{
					fontWeight: 600,
					fontSize: "16px",
					opacity: 0.79,
					color: "#333",
					width: "400px",
				}}
			>
				{props.data ? t("userSystem.editKey") : t("userSystem.addKey")}
			</DialogTitle>
			<DialogContent sx={{ paddingTop: "10px!important" }}>
				<Stack spacing={{ xy: 1, sm: 2 }}>
					<FormControl fullWidth error={isError && !formData.clientId}>
						<InputLabel color="success" required>{t("userSystem.clientName")}</InputLabel>
						<Select
							name="clientId"
							value={formData.clientId}
							onChange={handleChange}
							label={t("userSystem.clientName")}
							color="success"
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 350,
									},
								},
							}}
						>
							{props.clientData.map((item) => (<MenuItem value={item.id} key={item.id}>{item.clientName}</MenuItem>))}
						</Select>
					</FormControl>

					<TextField
						required
						name="keyCode"
						value={formData.keyCode}
						error={isError && !formData.keyCode}
						onChange={handleChange}
						label={t("userSystem.keyCode")}
						variant="outlined"
						color="success"
					/>

					<TextField
						required
						name="scope"
						value={formData.scope}
						error={isError && !formData.scope}
						onChange={handleChange}
						label={t("userSystem.scope")}
						variant="outlined"
						color="success"
					/>

					<TextField
						required
						name="idaasClientId"
						value={formData.idaasClientId}
						error={isError && !formData.idaasClientId}
						onChange={handleChange}
						label={t("userSystem.idaasClientId")}
						variant="outlined"
						color="success"
					/>

					<TextField
						required
						name="idaasClientSecrect"
						value={formData.idaasClientSecrect}
						error={isError && !formData.idaasClientSecrect}
						onChange={handleChange}
						label={t("userSystem.idaasClientSecrect")}
						variant="outlined"
						color="success"
					/>


					<FormControl fullWidth error={isError && !formData.scopeId?.length}>
						<InputLabel color="success" required>{t("userSystem.scopeId")}</InputLabel>
						<Select
							name="scopeId"
							value={formData.scopeId}
							multiple
							onChange={handleChange}
							label={t("userSystem.scopeId")}
							renderValue={(selected) => props.scopeData.filter((item: any) => selected.includes(item.id))
								.map(item => item.scopeName).join(',')}
							color="success"
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 350,
									},
								},
							}}
						>
							{props.scopeData.map((item) => (<MenuItem value={item.id} key={item.id}>{item.scopeName}</MenuItem>))}
						</Select>
					</FormControl>

					<LocalizationProvider
						dateAdapter={AdapterDayjs}
						adapterLocale={context.language === "cn" ? "zh" : "en"}

					>
						<DatePicker
							label={t("userSystem.resetTime") + " *"}
							value={formData.resetTime}
							format="YYYY-MM-DD"
							onChange={(event) => {
								handleChange({ target: { name: "resetTime", value: event } });
							}}
							sx={{
								"& .MuiInputLabel-root": {
									color: isError && !formData.resetTime ? "#d32f2f" : "",
								},
								"& .MuiPickersOutlinedInput-notchedOutline,& .Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
									borderColor: isError && !formData.resetTime ? "#d32f2f!important" : "",
								},
								"& .MuiInputLabel-root.Mui-focused": {
									color: isError && !formData.resetTime ? "#d32f2f" : "green",
								},
								"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
									borderColor: isError && !formData.resetTime ? "#d32f2f" : "green",
								},
							}}
						/>
						<DatePicker
							label={t("userSystem.startDate") + " *"}
							value={formData.startTime}
							format="YYYY-MM-DD"
							onChange={(event) => {
								handleChange({ target: { name: "startTime", value: event } });
							}}
							maxDate={formData.endTime}
							sx={{
								"& .MuiInputLabel-root": {
									color: isError && !formData.startTime ? "#d32f2f" : "",
								},
								"& .MuiPickersOutlinedInput-notchedOutline,& .Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
									borderColor: isError && !formData.startTime ? "#d32f2f!important" : "",
								},
								"& .MuiInputLabel-root.Mui-focused": {
									color: isError && !formData.startTime ? "#d32f2f" : "green",
								},
								"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
									borderColor: isError && !formData.startTime ? "#d32f2f" : "green",
								},
							}}
						/>
						<DatePicker
							label={t("userSystem.endDate") + " *"}
							value={formData.endTime}
							format="YYYY-MM-DD"
							onChange={(event) => {
								handleChange({ target: { name: "endTime", value: event } });
							}}
							minDate={formData.startTime}
							sx={{
								"& .MuiInputLabel-root": {
									color: isError && !formData.endTime ? "#d32f2f" : "",
								},
								" .MuiPickersOutlinedInput-notchedOutline,& .Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
									borderColor: isError && !formData.endTime ? "#d32f2f!important" : "",
								},
								"& .MuiInputLabel-root.Mui-focused": {
									color: isError && !formData.endTime ? "#d32f2f" : "green",
								},
								"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
									borderColor: isError && !formData.endTime ? "#d32f2f" : "green",
								},
							}}
						/>
					</LocalizationProvider>

					<TextField
						multiline
						label={t("userSystem.remark")}
						name="remark"
						value={formData.remark}
						onChange={handleChange}
						color="success"
						inputProps={{
							style: {
								resize: "vertical",
							},
							maxLength: 50
						}}
						rows={2}
						sx={{
							marginBottom: "15px",
						}}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={close}
					color="success"
				>
					{t("buttons.cancel")}
				</Button>
				<Button
					onClick={handleSave}
					color="success"
					variant="contained"
				>
					{t("buttons.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default AddModal;
